import React, { useState } from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { showcaseSyncDisplay } from './useShowcaseProductSync';

const readDismissal = key => {
  try { return !!key && window.localStorage.getItem(key) === 'true'; }
  catch (_) { return false; }
};

export default function ShowcaseProductSyncStatus({ sync, totalProducts = 0, instanceUniqueId }) {
  const display = showcaseSyncDisplay(sync);
  const key = instanceUniqueId && sync.session?.id
    ? `promoshowcase:products:sync-status:dismissed:${encodeURIComponent(instanceUniqueId)}:${encodeURIComponent(sync.session.id)}` : null;
  const [dismissal, setDismissal] = useState(() => ({ key, hidden: readDismissal(key) }));
  const hidden = dismissal.key === key ? dismissal.hidden : readDismissal(key);
  const canDismiss = !!key && !sync.active && !sync.refreshing && !sync.cancelPending
    && ['complete', 'cancelled'].includes(display?.state) && sync.session.status === display.state;
  const dismiss = () => {
    if (!canDismiss) return;
    setDismissal({ key, hidden: true });
    try { window.localStorage.setItem(key, 'true'); }
    catch (_) { /* A blocked preference store must not prevent local dismissal. */ }
  };
  if (!display || (canDismiss && hidden)) return null;
  const processed = Math.max(0, Number(sync.session?.processedCount) || 0);
  const total = Math.max(0, Number(sync.session?.expectedCount ?? totalProducts) || 0);
  const percent = total ? Math.min(100, Math.round(processed / total * 100)) : 0;
  return <Box sx={{ display: 'grid', gap: .5 }} role="status" aria-live="polite">
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{display.label}</Typography>
      {canDismiss && <Button size="small" aria-label="Dismiss sync status" onClick={dismiss}>Dismiss</Button>}
    </Box>
    {sync.session?.status === 'running' && sync.cancel && <Button size="small" color="warning" disabled={sync.cancelPending} onClick={() => window.confirm('Cancel this sync attempt? A remote request already in progress may still finish. Existing remote writes will not be rolled back.') && sync.cancel()}>{sync.cancelPending ? 'Cancelling…' : 'Cancel sync'}</Button>}
    {display.message && <Alert severity={display.state === 'paused' || display.state === 'partial' ? 'warning' : 'info'}>{display.message}</Alert>}
    <LinearProgress variant="determinate" value={percent} aria-label="Website product sync progress" />
    <Typography variant="caption">{processed.toLocaleString()} / {total.toLocaleString()} product{total === 1 ? '' : 's'} · {percent}%</Typography>
    {display.state === 'paused' && <Typography variant="caption">This browser has stopped sending batches. Another request may still be active elsewhere.</Typography>}
    {sync.refreshFailed && <Typography variant="caption">Status refresh unavailable; last known progress is shown.</Typography>}
  </Box>;
}
